import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavigateOptions, Outlet, To, useBeforeUnload, useNavigate } from "react-router-dom";
import { Col, Container, Row, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { useAuth, useUser, useUserPermissions } from "../../utils/auth";
import i18n from "../../i18n";
import { updateLanguageAPI } from "../../features/auth/api/authAPI";
import SidebarMain from "../SidebarMain";
import { PERMISSIONS } from "../../constants/permissions";
import { clearAllFilters } from "../../redux/filtersSlice";
import ModalGlobal from "../AppModal/ModalGlobal";
import { useModal } from "../../hooks/useModal";
import { NavigationGuardConfig, NavigationGuardContext } from "../../contexts/NavigationGuardContext";
import ClientPreferencesForm from "./preferences/ClientPreferencesForm";
import NotificationsUserList from "../../features/notifications/pages/NotificationsUserList";
import { getClientParamsSchema } from "../../features/admin/generalParams/schema/clientParamsSchema";
import { clearErrors, setError } from "../../redux/clientParamsSlice";
import { RootState } from "../../redux/store";
import { updateGeneralParams } from "../../features/admin/generalParams/api/generalParamsAPI";
import { ApiErrorResponse } from "../../types/ApiErrorResponse";
import bellIcon from "@/assets/notification_bell_blue.svg";
import bellIconHasNotifications from "@/assets/notification_hasnotification_bell.svg";
import { useOrganizationalStructuresSelectByUser } from "../../features/admin/Dictionaries/hooks/useOrganizationalStructuresSelect";
import AppHierarchicalMultiSelect from "../AppHierarchicalMultiSelect/AppHierarchicalMultiSelect";
import { updateCurrentUserField } from "../../redux/currentUserSlice";

export default function MainLayout() {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const userInfo = useUser();
    const dispatch = useDispatch();
    const permissions = useUserPermissions();
    const { t } = useTranslation(["auth", "common"]);
    const { showModal, openModal, closeModal } = useModal();
    const [showPreferencesModal, setShowPreferencesModal] = useState(false);
    const [showNotificationsModal, setShowNotificationsModal] = useState(false);
    const [isNotificationsDeleteModalOpen, setIsNotificationsDeleteModalOpen] = useState(false);
    const [notificationsRefreshKey, setNotificationsRefreshKey] = useState(0);
    const [preferencesMessage, setPreferencesMessage] = useState("");
    const [preferencesMessageKey, setPreferencesMessageKey] = useState(0);
    const notificationsDropdownRef = useRef<HTMLDivElement>(null);
    const notificationsButtonRef = useRef<HTMLButtonElement>(null);

    const { data: organizationalStructures, refetch } = useOrganizationalStructuresSelectByUser({});
    const clientParamsForm = useSelector((state: RootState) => state.clientParamsForm.data);
    const currentUserInfo = useSelector((state: RootState) => state.currentUserInfo.data);

    useEffect(() => {
        if (!organizationalStructures)
            refetch
    }, [])

    const [navigationGuard, setNavigationGuard] = useState<NavigationGuardConfig>({
        enabled: false,
        message: "",
        messageButtonSave: "",
        messageButtonClose: ""
    });
    const [pendingNavigation, setPendingNavigation] = useState<{
        to: To;
        options?: NavigateOptions;
    } | null>(null);

    const handleLogout = () => {
        dispatch(clearAllFilters());
        logout();
    };

    const navigateWithGuard = useCallback((to: To, options?: NavigateOptions) => {
        if (navigationGuard.enabled) {
            setPendingNavigation({ to, options });
            if (!showModal) {
                openModal(null, "", "", "info", "");
            }
            return;
        }
        navigate(to, options);
    }, [navigate, navigationGuard.enabled, openModal, showModal]);

    const handleAdmin = () => {
        navigateWithGuard("/admin");
    };

    const changeLanguage = async (language: string) => {
        await updateLanguageAPI(language);
        i18n.changeLanguage(language);
        localStorage.setItem("lang", language);
    };

    const showModalPreferences = () => {
        setPreferencesMessage("");
        setShowPreferencesModal(true);
    };

    const closeModalPreferences = () => {
        setPreferencesMessage("");
        setShowPreferencesModal(false);
    };

    const toggleNotificationsDropdown = () => {
        setShowNotificationsModal((prev) => {
            const next = !prev;
            if (next) {
                setNotificationsRefreshKey((currentKey) => currentKey + 1);
            }
            return next;
        });
    };

    const closeNotificationsDropdown = useCallback((restoreFocus = false) => {
        setShowNotificationsModal(false);
        if (restoreFocus) {
            notificationsButtonRef.current?.focus();
        }
    }, []);

    useEffect(() => {
        if (!showNotificationsModal) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (isNotificationsDeleteModalOpen) return;

            if (
                notificationsDropdownRef.current &&
                !notificationsDropdownRef.current.contains(event.target as Node)
            ) {
                closeNotificationsDropdown();
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (isNotificationsDeleteModalOpen) return;

            if (event.key === "Escape") {
                closeNotificationsDropdown(true);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [closeNotificationsDropdown, isNotificationsDeleteModalOpen, showNotificationsModal]);

    useBeforeUnload(
        useCallback((event) => {
            if (!navigationGuard.enabled) return;
            event.preventDefault();
            event.returnValue = "";
        }, [navigationGuard.enabled])
    );

    const handleCancelNavigation = () => {
        closeModal();
        if (pendingNavigation) {
            navigate(pendingNavigation.to, pendingNavigation.options);
            setPendingNavigation(null);
        }
        setNavigationGuard({
            enabled: false,
            message: "",
            messageButtonSave: "",
            messageButtonClose: "",
        });
    };

    const handleSaveNavigation = async () => {
        closeModal();
        if (navigationGuard.onSave) {

            await navigationGuard.onSave(
                typeof pendingNavigation?.to === "string"
                    ? pendingNavigation.to
                    : pendingNavigation?.to.pathname,
                pendingNavigation?.options?.state
            );
        }
        if (pendingNavigation) {

            navigate(
                pendingNavigation.to,
                pendingNavigation.options
            );

            setPendingNavigation(null);
        }

        setNavigationGuard({
            enabled: false,
            message: "",
            messageButtonSave: "",
            messageButtonClose: "",
        });
    };

    const onSubmit = async () => {
        const schema = getClientParamsSchema(t);
        dispatch(clearErrors());

        try {
            await schema.validate(clientParamsForm, { abortEarly: false });

            await updateGeneralParams(clientParamsForm);
            setPreferencesMessageKey((prev) => prev + 1);
            setPreferencesMessage(t("clientParams:saveUserPreferencesSuccessfully"));

        } catch (err: any) {
            if (Array.isArray(err?.inner) && err.inner.length > 0) {
                err.inner.forEach((error: any) => {
                    dispatch(setError({
                        field: error.path,
                        message: error.message
                    }));
                });
                return;
            }

            const apiError = err as ApiErrorResponse;
            setPreferencesMessage(apiError?.message || t("common:error"));
        }
    }

    return (
        <>
            <ModalGlobal
                show={showModal}
                onCancel={handleCancelNavigation}
                onClose={closeModal}
                body={navigationGuard.message}
                onSave={handleSaveNavigation}
                saveText={navigationGuard.messageButtonSave}
                closeText={navigationGuard.messageButtonClose}
                isLoading={false}
            />
            <ModalGlobal
                show={showPreferencesModal}
                onClose={closeModalPreferences}
                onSave={onSubmit}
                title={t("clientParams:preferences.modalPreferencesTitle")}
                body={<ClientPreferencesForm messageName={preferencesMessage} messageKey={preferencesMessageKey}/>}
                closeText={t("common:buttons.buttonCancel")}
                isLoading={false}
                size="lg"
                saveText={t("common:buttons.buttonSave")}
                dialogClassName="preferences-modal"
            />
            <NavigationGuardContext.Provider value={{ setNavigationGuard, navigateWithGuard }}>
                <Container fluid className="bg-light p-0">
                    <div className="navbar navbar-light bg-light shadow-sm">
                        <div className="container-fluid">
                            <Link to="/claims/dashboard" style={{ textDecoration: 'none', color: 'black' }}>
                                <img
                                    src={userInfo.userInfo?.clientLogo
                                        ? `data:${userInfo.userInfo.clientLogoContentType};base64,${userInfo.userInfo.clientLogo}`
                                        : "/logo-craiteria.svg"
                                    }
                                    alt="Craiteria"
                                    style={{ width: '200px' }}
                                />
                            </Link>
                            <div className="d-flex align-items-center">
                                <AppHierarchicalMultiSelect
                                    style={{width: "450px"}}
                                    placeholder={t("menu:allOrganizationalStructures")}
                                    options={organizationalStructures ?? []}
                                    selectedValues={(currentUserInfo.selectedOrganizationalStructureIds ?? [])}
                                    onChange={(values) => dispatch(updateCurrentUserField({ field: 'selectedOrganizationalStructureIds', value: values }))}
                                />

                                    <div className="alerts-dropdown" ref={notificationsDropdownRef}>
                                        <OverlayTrigger
                                            placement="left"
                                            overlay={
                                                <Tooltip id="tooltip-info">
                                                    {t("common:buttons.buttonToOpenNotifications")}
                                                </Tooltip>
                                            }
                                        >
                                            <button
                                                ref={notificationsButtonRef}
                                                onClick={toggleNotificationsDropdown}
                                                className="navbar-toggler"
                                                type="button"
                                                aria-label="Toggle notifications"
                                                aria-controls="notifications-dropdown-panel"
                                                aria-expanded={showNotificationsModal}
                                            >
                                                {userInfo.userInfo.hasNotifications ? (
                                                    <img
                                                        src={bellIconHasNotifications}
                                                        alt="Notifications"
                                                        style={{ width: '50px' }}
                                                    />
                                                ) : (
                                                    <img
                                                        src={bellIcon}
                                                        alt="Notifications"
                                                        style={{ width: '50px' }}
                                                    />
                                                )}
                                            </button>
                                        </OverlayTrigger>
                                        {showNotificationsModal && (
                                            <NotificationsUserList
                                                dropdownId="notifications-dropdown-panel"
                                                refreshKey={notificationsRefreshKey}
                                                onDeleteModalOpenChange={setIsNotificationsDeleteModalOpen}
                                            />
                                        )}
                                    </div>

                                {permissions.length > 0 && permissions.includes(PERMISSIONS.ADMINISTRATION.VIEW) && (
                                    <OverlayTrigger
                                        placement="left"
                                        overlay={
                                            <Tooltip id="tooltip-info">
                                                {t("common:buttons.buttonGoToAdministrator")}
                                            </Tooltip>
                                    }>
                                        <button onClick={handleAdmin} className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarHeader" aria-controls="navbarHeader" aria-expanded="false" aria-label="Toggle navigation">
                                            <img
                                                src="/ico-index.png"
                                                alt="Index"
                                                style={{ width: '50px', }}

                                            />
                                        </button>
                                    </OverlayTrigger>
                                )}
                                <Dropdown>
                                    <Dropdown.Toggle as="div" id="custom-dropdown" className="navbar-toggler" style={{ cursor: 'pointer' }}>
                                        <img
                                            src="/ico-user.png"
                                            alt="User"
                                            style={{ width: '50px', }}
                                        />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="dropdown-menu-text-black" style={{ minWidth: "auto", width: "190px", padding: "0" }}>
                                        <Dropdown drop="end">
                                            <Dropdown.Toggle as="div" className="dropdown-item" style={{ cursor: "pointer", padding: "0.25rem 1rem" }}>
                                                    {t("auth:languages")}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu style={{ minWidth: "auto", width: "100px" }}>
                                                <Dropdown.Item onClick={async () => await changeLanguage("ca")} style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>CA</Dropdown.Item>
                                                <Dropdown.Item onClick={async () => await changeLanguage("en")} style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>EN</Dropdown.Item>
                                                <Dropdown.Item onClick={async () => await changeLanguage("es")} style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>ES</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                        <Dropdown.Item onClick={showModalPreferences} style={{ whiteSpace: "normal", overflow: "visible" }}>{t("auth:buttonModalPreferences")}</Dropdown.Item>
                                        <Dropdown.Item onClick={handleLogout} style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>{t("auth:buttonLogOut")}</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                </Container>
                <div style={{ display: 'flex', height: '' }}>
                    <SidebarMain />
                    <Container fluid className="flex-grow-1 pl-4 pt-4 min-w-0">
                        <Row>
                            <Col md={12}>
                                <Outlet />
                            </Col>
                        </Row>
                    </Container>
                </div>
                <Container fluid>
                    <Row className="justify-content-between py-2" id="footer">
                        <div className="container">
                            <Col>
                            <p>� 2024 - CraiterIA  - <a href="/Claims/TermsOfService">Terms of Service</a></p>
                            </Col>
                        </div>
                    </Row>
                </Container>
            </NavigationGuardContext.Provider>
        </>
    );
}
